import { ClaimWorkflowConfigurationOption } from '../../../api';
import React, { useMemo } from 'react';
import { ISelectOption } from '../../base';
import { WorkflowConfigValue } from '../../../services/worker-configuration/worker-configuration.types';
import { WorkerStore } from '../../../stores/worker-configuration/worker.store';
import {
    coerceConfigValue,
    formatAllowedOptions,
    formatEditorValue,
    getEnumSelectOptions,
    humanizeKey,
    parseBooleanTriState,
    parseOptionEditor,
    sanitizeNumericConfigInput,
} from '../configuration.utils';
import { ConfigurationRulePayerEditor } from '../rules/configuration-rule-payer-editor';
import { ConfigurationTagListEditor } from '../rules/configuration-tag-list-editor';
import * as S from './option-field.styles';

type ConfiguredOption = ClaimWorkflowConfigurationOption & { key: string };

interface IOptionFieldProps {
    option: ConfiguredOption;
    currentValue: WorkflowConfigValue | undefined;
    onChange: (value: WorkflowConfigValue | null) => void;
    disabled?: boolean;
    status: string;
    store: WorkerStore;
}

function asStringList(value: WorkflowConfigValue | undefined): string[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.filter((entry): entry is string => typeof entry === 'string' && entry.trim() !== '');
}

function isTagListOption(option: ConfiguredOption): boolean {
    return option.key === 'codingClaimTags' || /ClaimTags$/i.test(option.key);
}

function isPayerListOption(option: ConfiguredOption): boolean {
    if (isTagListOption(option)) {
        return false;
    }

    return option.valueType === 'list' || /PayerList$/i.test(option.key);
}

interface IBooleanFieldProps {
    value: WorkflowConfigValue | undefined;
    onChange: (value: WorkflowConfigValue | null) => void;
    disabled?: boolean;
}

const BooleanField: React.FC<IBooleanFieldProps> = ({ value, onChange, disabled }) => {
    const triState = parseBooleanTriState(value);

    return (
        <S.SegmentedControl aria-disabled={disabled}>
            <S.SegmentButton
                type="button"
                aria-pressed={triState === true}
                active={triState === true}
                disabled={disabled}
                onClick={() => {
                    if (!disabled) {
                        onChange(true);
                    }
                }}>
                True
            </S.SegmentButton>
            <S.SegmentButton
                type="button"
                variant="neutral"
                aria-pressed={triState === false}
                active={triState === false}
                disabled={disabled}
                onClick={() => {
                    if (!disabled) {
                        onChange(false);
                    }
                }}>
                False
            </S.SegmentButton>
            <S.SegmentButtonUnset
                type="button"
                aria-pressed={triState === null}
                active={triState === null}
                disabled={disabled}
                onClick={() => {
                    if (!disabled) {
                        onChange(null);
                    }
                }}>
                Default
            </S.SegmentButtonUnset>
        </S.SegmentedControl>
    );
};

interface IEnumFieldProps {
    value: WorkflowConfigValue | undefined;
    choices: string[];
    valueType?: string;
    onChange: (value: WorkflowConfigValue | null) => void;
    disabled?: boolean;
}

const EnumField: React.FC<IEnumFieldProps> = ({ value, choices, valueType, onChange, disabled }) => {
    const editor = useMemo(() => ({ kind: 'enum' as const, choices }), [choices]);
    const selectValue = formatEditorValue(value, editor, valueType);
    const options: ISelectOption<string>[] = [
        { label: 'Not set', value: '' },
        ...getEnumSelectOptions(editor).map(
            option =>
                ({
                    label: option.label,
                    value: option.value,
                }) as ISelectOption<string>,
        ),
    ];
    const selectedOption = options.find(option => option.value === selectValue) ?? null;

    return (
        <S.EnumSelect
            options={options}
            value={selectedOption}
            required
            disabled={disabled}
            onChange={selected => {
                const option = selected as ISelectOption<string> | null;
                const next = option?.value ?? '';
                onChange(next === '' ? null : coerceConfigValue(next, editor, valueType));
            }}
        />
    );
};

interface IFreeInputFieldProps {
    value: WorkflowConfigValue | undefined;
    editor: Extract<ReturnType<typeof parseOptionEditor>, { kind: 'number' | 'text' }>;
    valueType?: string;
    onChange: (value: WorkflowConfigValue | null) => void;
    disabled?: boolean;
}

const FreeInputField: React.FC<IFreeInputFieldProps> = ({ value, editor, valueType, onChange, disabled }) => {
    const displayValue = formatEditorValue(value, editor, valueType);
    const isNumeric = editor.kind === 'number';

    return (
        <S.TextInput
            type="text"
            inputMode={isNumeric ? 'numeric' : 'text'}
            pattern={isNumeric ? '[0-9]*' : undefined}
            value={displayValue}
            placeholder={editor.placeholder}
            disabled={disabled}
            readOnly={disabled}
            onChange={event => {
                if (disabled) {
                    return;
                }

                const raw = event.target.value;
                if (isNumeric) {
                    const digits = sanitizeNumericConfigInput(raw);
                    onChange(digits === '' ? null : coerceConfigValue(digits, editor, valueType));
                    return;
                }

                onChange(raw === '' ? null : coerceConfigValue(raw, editor, valueType));
            }}
        />
    );
};

export const OptionField: React.FC<IOptionFieldProps> = ({
    option,
    currentValue,
    onChange,
    disabled,
    status,
    store,
}) => {
    const editor = useMemo(
        () => parseOptionEditor({ valueType: option.valueType, options: option.options }),
        [option.options, option.valueType],
    );
    const usePayerListEditor = isPayerListOption(option);
    const useTagListEditor = isTagListOption(option);
    const useStackLayout = usePayerListEditor || useTagListEditor;

    const renderEditor = (): React.ReactNode => {
        if (useTagListEditor) {
            return (
                <ConfigurationTagListEditor
                    tagNames={asStringList(currentValue)}
                    allowSearch={store.apiMode === 'support'}
                    disabled={disabled}
                    onChange={onChange}
                />
            );
        }

        if (usePayerListEditor) {
            return (
                <ConfigurationRulePayerEditor
                    pickerKey={`config|${status}|${option.key}|payer`}
                    payerIds={asStringList(currentValue)}
                    store={store}
                    disabled={disabled}
                    showMatchHint={false}
                    onChange={onChange}
                />
            );
        }

        switch (editor.kind) {
            case 'boolean':
                return <BooleanField value={currentValue} onChange={onChange} disabled={disabled} />;
            case 'enum':
                return (
                    <EnumField
                        value={currentValue}
                        choices={editor.choices}
                        valueType={option.valueType}
                        onChange={onChange}
                        disabled={disabled}
                    />
                );
            case 'number':
            case 'text':
                return (
                    <FreeInputField
                        value={currentValue}
                        editor={editor}
                        valueType={option.valueType}
                        onChange={onChange}
                        disabled={disabled}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <S.OptionItem data-layout={useStackLayout ? 'stack' : undefined}>
            <S.OptionContent>
                <S.OptionTitleRow>
                    <S.OptionLabel>{humanizeKey(option.key)}</S.OptionLabel>
                    <S.OptionKey>{option.key}</S.OptionKey>
                </S.OptionTitleRow>
                {option.description && <S.OptionDescription>{option.description}</S.OptionDescription>}
                {!useStackLayout && editor.kind === 'enum' && option.options && (
                    <S.OptionAllowed>
                        <S.OptionAllowedLabel>Allowed: </S.OptionAllowedLabel>
                        {formatAllowedOptions(option.options)}
                    </S.OptionAllowed>
                )}
            </S.OptionContent>
            <S.OptionControl
                data-readonly={disabled ? 'true' : undefined}
                data-layout={useStackLayout ? 'stack' : undefined}>
                {renderEditor()}
            </S.OptionControl>
        </S.OptionItem>
    );
};
